import React from 'react'
import Link from 'next/link'
import { Project } from '@/types'
import { formatDateRange } from '@/lib/utils'
import { motion } from 'framer-motion'
import OptimizedImage from '@/components/ui/OptimizedImage'

interface ProjectCardProps {
  project: Project
  index?: number
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, index = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="h-full"
    >
      <Link
        href={`/projects/${project.slug.current}`}
        className="block h-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
        aria-label={`View details for ${project.title} project`}
      >
        <article className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col transform hover:-translate-y-1 focus-within:ring-2 focus-within:ring-primary">
          <div className="relative h-48">
            <OptimizedImage
              image={project.featuredImage}
              alt={project.title}
              fill
              className="object-cover"
              fallbackText="No project image"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              priority={index < 4} // Prioritize loading for first 4 visible projects
            />

            {/* Project type badge */}
            <div className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
              {project.projectType}
            </div>
          </div>

          <div className="p-6 flex-grow flex flex-col">
            <h3 className="text-xl font-bold mb-2" id={`project-title-${project.slug.current}`}>
              {project.title}
            </h3>

            <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 flex-grow">
              {project.description?.[0]?.children?.[0]?.text || 'No description available'}
            </p>

            <div className="mt-auto">
              {/* Date range */}
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {formatDateRange(project.startDate, project.endDate)}
              </div>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2">
                {project.technologies?.slice(0, 3).map(tech => (
                  <span
                    key={tech._id}
                    className="inline-block bg-gray-100 dark:bg-gray-600 rounded-full px-3 py-1 text-sm"
                  >
                    {tech.name}
                  </span>
                ))}
                {project.technologies?.length > 3 && (
                  <span className="inline-block bg-gray-100 dark:bg-gray-600 rounded-full px-3 py-1 text-sm">
                    +{project.technologies.length - 3} more
                  </span>
                )}
              </div>

              {/* Blockchain details indicator */}
              {project.blockchainDetails && (
                <div className="mt-3 flex items-center text-sm text-primary-dark dark:text-primary-light">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Blockchain Details
                </div>
              )}
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  )
}

export default ProjectCard
